/**
 * discoverEntryTarget — where an inbound "Discover more Wheels like this" click
 * should land. Pure, DB-free, no React: the routing decision is the part worth
 * asserting, so it lives apart from the component that renders it (this repo's
 * established split — see categoryUrlSync.js, heroLogic.js, forYouLean.js).
 *
 * Discover redesign piece 4 (DL-098). Spec: someone who arrived on iYuga by
 * opening SOMEONE ELSE'S Wheel did not come through the front door and has no
 * way to find Discover. Measured 2026-08-01, anonymous, bundle main.f563cd08.js:
 * the Wheel-view page renders NO header, NO logo and NO nav for a logged-out
 * visitor — every control on it was Follow / Message / comment / media tabs /
 * Login / Sign Up. There was literally no path in.
 *
 * "Like this" is honoured by carrying the Wheel's own category through as the
 * ?category= filter Discover already understands, so the arrival lands on more
 * of what they were just looking at rather than on a generic feed.
 */
import { EXPLORE_CATEGORIES, EXPLORE_ALL_CATEGORY } from "../../pages/ExploreV2/constants";

/** The Discover route itself. One constant so a rename is one edit.
 * ISP-9999 (Gordon 2026-08-09): URL renamed /explore -> /discover to match the
 * "Discover" label. /explore still resolves via a redirect in routes.js. */
export const DISCOVER_PATH = "/discover";

const VALID_SLUGS = new Set(EXPLORE_CATEGORIES.map((c) => c.slug));

/**
 * Resolve the target URL for a "Discover more like this" click.
 *
 * Fails OPEN, never closed: an absent, unknown, malformed or non-string
 * category yields the unfiltered feed. Landing on all of Discover is a fine
 * outcome; landing on `?category=undefined` (an empty feed with no explanation)
 * is the acquisition surface's worst possible first impression.
 *
 * @param {unknown} category a Wheel's `category` value, straight off the API
 * @returns {string} a path, always safe to navigate to
 */
export const discoverTargetFor = (category) => {
  if (typeof category !== "string") return DISCOVER_PATH;
  const slug = category.trim().toLowerCase();
  if (!slug || slug === EXPLORE_ALL_CATEGORY) return DISCOVER_PATH;
  if (!VALID_SLUGS.has(slug)) return DISCOVER_PATH;
  return `${DISCOVER_PATH}?category=${encodeURIComponent(slug)}`;
};

/**
 * The CTA's label. Category-specific when we know it, because "Discover more
 * Fitness Wheels" tells the visitor what they are about to get; the generic
 * form is the fallback, never a lie about a filter that is not applied.
 */
export const discoverCtaLabelFor = (category) => {
  if (typeof category !== "string") return "Discover more Wheels";
  const slug = category.trim().toLowerCase();
  const match = EXPLORE_CATEGORIES.find((c) => c.slug === slug);
  return match ? `Discover more ${match.label} Wheels` : "Discover more Wheels";
};
